import * as React from 'react';

import {
  enumCurrencyCode,
  enumUnit,
  Unit,
} from '@rematter/frontend-lib-api-client';
import {
  convertFromPounds,
  convertToPounds,
  humanReadableShortPricingUnit,
  shortenUnit,
} from '@rematter/ui-web';
import Big from 'big.js';

import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import {
  BrokeredLoadFormLineItem,
  enumBrokeredLoadWeightType,
} from '~/modules/brokerage/types/brokeredLoad';
import { WeightUnitType } from '~/modules/trade/types/load';
import {
  DeleteButtonCell,
  HeaderCell,
  InventoryItemSelect,
  LineItemHeader,
  LineItemRow,
  LineItemTable,
  PriceFieldCell,
  UnitFieldCell,
  WeightFieldCell,
} from '~/shared/components/line-items';
import { SelectPurchaseOrder } from '~/shared/components/select-fields/SelectPurchaseOrder';

const BROKERED_LOAD_SHIPPED_GRID =
  'grid-cols-[2fr_1.5fr_1fr_100px_1fr_1fr_0.8fr_var(--size-input)]';

interface BrokeredLoadShippedTableProps {
  lineItems: BrokeredLoadFormLineItem[];
  disabled?: boolean;
  purchaseOrderIds?: string[];
  weightUnit?: WeightUnitType;
  onUpdateLineItem: (
    id: string,
    updates: Partial<BrokeredLoadFormLineItem>,
  ) => void;
  onDeleteLineItem: (id: string) => void;
  onAddLineItem: () => void;
}

export const BrokeredLoadShippedTable: React.FC<
  BrokeredLoadShippedTableProps
> = ({
  lineItems,
  disabled = false,
  purchaseOrderIds,
  weightUnit = 'price',
  onUpdateLineItem,
  onDeleteLineItem,
  onAddLineItem,
}) => {
  const t = useTranslations();
  const isEmpty = lineItems.length === 0;

  const getDisplayUnit = React.useCallback(
    (pricingUnit: string | null | undefined): Unit =>
      weightUnit === 'price'
        ? (pricingUnit as Unit) || enumUnit.Pounds
        : enumUnit.Pounds,
    [weightUnit],
  );

  const toDisplay = React.useCallback(
    (
      weight: { value?: number; unit?: string } | null | undefined,
      displayUnit: Unit,
    ): number => {
      if (!weight?.value) return 0;
      if (weight.unit === displayUnit) return weight.value;
      return convertFromPounds(
        convertToPounds(weight.value, weight.unit as Unit),
        displayUnit,
      );
    },
    [],
  );

  const fromDisplay = React.useCallback(
    (value: number, displayUnit: Unit, storedUnit: Unit): number => {
      if (storedUnit === displayUnit) return value;
      return convertFromPounds(convertToPounds(value, displayUnit), storedUnit);
    },
    [],
  );

  const handleGrossWeightChange = React.useCallback(
    (lineItem: BrokeredLoadFormLineItem, displayValue: number) => {
      const tare = lineItem.weights?.[enumBrokeredLoadWeightType.Tare];
      const storedUnit = (tare?.unit ?? enumUnit.Pounds) as Unit;
      const displayUnit = getDisplayUnit(lineItem.pricingUnit);
      const storedValue = fromDisplay(displayValue, displayUnit, storedUnit);

      const netValue = new Big(storedValue).minus(tare?.value ?? 0).toNumber();

      onUpdateLineItem(lineItem.id, {
        weights: {
          ...lineItem.weights,
          [enumBrokeredLoadWeightType.Gross]: {
            value: storedValue,
            unit: storedUnit,
            __typename: 'Weight',
          },
          [enumBrokeredLoadWeightType.Net]: {
            value: netValue,
            unit: storedUnit,
            __typename: 'Weight',
          },
        },
      });
    },
    [onUpdateLineItem, getDisplayUnit, fromDisplay],
  );

  const handleTareWeightChange = React.useCallback(
    (lineItem: BrokeredLoadFormLineItem, displayValue: number) => {
      const gross = lineItem.weights?.[enumBrokeredLoadWeightType.Gross];
      const storedUnit = (gross?.unit ?? enumUnit.Pounds) as Unit;
      const displayUnit = getDisplayUnit(lineItem.pricingUnit);
      const storedValue = fromDisplay(displayValue, displayUnit, storedUnit);
      const netValue = new Big(gross?.value ?? 0).minus(storedValue).toNumber();

      onUpdateLineItem(lineItem.id, {
        weights: {
          ...lineItem.weights,
          [enumBrokeredLoadWeightType.Tare]: {
            value: storedValue,
            unit: storedUnit,
            __typename: 'Weight',
          },
          [enumBrokeredLoadWeightType.Net]: {
            value: netValue,
            unit: storedUnit,
            __typename: 'Weight',
          },
        },
      });
    },
    [onUpdateLineItem, getDisplayUnit, fromDisplay],
  );

  return (
    <LineItemTable
      isEmpty={isEmpty}
      onAdd={onAddLineItem}
      hideAddButton={true}
      disabled={disabled}
      addButtonLabel={t.brokerage.loads.detail.materials.addMaterial}
      emptyTitle={t.brokerage.loads.detail.materials.emptyShippedTitle}
      emptyDescription={
        t.brokerage.loads.detail.materials.emptyShippedDescription
      }
      className="@BrokeredLoadShippedTable"
    >
      <LineItemHeader
        gridTemplate={BROKERED_LOAD_SHIPPED_GRID}
        className="px-2 py-1"
      >
        <HeaderCell className="text-sm font-semibold text-neutral">
          {t.brokerage.loads.detail.materials.columns.material}
        </HeaderCell>
        <HeaderCell className="text-sm font-semibold text-neutral">
          {t.brokerage.loads.detail.materials.columns.purchaseOrder}
        </HeaderCell>
        <HeaderCell className="text-sm font-semibold text-neutral">
          {t.brokerage.loads.detail.materials.columns.unitPrice}
        </HeaderCell>
        <HeaderCell className="text-sm font-semibold text-neutral">
          {t.brokerage.loads.detail.materials.columns.pricingUnit}
        </HeaderCell>
        <HeaderCell className="text-sm font-semibold text-neutral">
          {t.brokerage.loads.detail.materials.columns.gross}
        </HeaderCell>
        <HeaderCell className="text-sm font-semibold text-neutral">
          {t.brokerage.loads.detail.materials.columns.tare}
        </HeaderCell>
        <HeaderCell className="text-sm font-semibold text-neutral">
          {t.brokerage.loads.detail.materials.columns.net}
        </HeaderCell>
        {!disabled && <HeaderCell />}
      </LineItemHeader>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {lineItems.map((lineItem) => {
          const pricingUnitSuffix = lineItem.pricingUnit
            ? `/${shortenUnit(lineItem.pricingUnit as Unit)}`
            : undefined;

          const grossWeight =
            lineItem.weights?.[enumBrokeredLoadWeightType.Gross];
          const tareWeight =
            lineItem.weights?.[enumBrokeredLoadWeightType.Tare];
          const netWeight = lineItem.weights?.[enumBrokeredLoadWeightType.Net];
          const displayUnit = getDisplayUnit(lineItem.pricingUnit);
          const displaySuffix = humanReadableShortPricingUnit(displayUnit);

          return (
            <LineItemRow
              key={lineItem.id}
              gridTemplate={BROKERED_LOAD_SHIPPED_GRID}
              className="w-full gap-3 px-2 py-1.5"
            >
              <InventoryItemSelect
                value={lineItem.itemId}
                onChange={(itemId) =>
                  onUpdateLineItem(lineItem.id, { itemId: itemId ?? null })
                }
                disabled={disabled}
                placeholder={t.brokerage.loads.detail.materials.selectMaterial}
                className="min-w-0 overflow-hidden"
              />
              <SelectPurchaseOrder
                value={lineItem.purchaseOrderId}
                onChange={(purchaseOrderId) =>
                  onUpdateLineItem(lineItem.id, {
                    purchaseOrderId: purchaseOrderId ?? null,
                  })
                }
                ids={purchaseOrderIds}
                isDisabled={disabled}
                className="min-w-0 overflow-hidden"
              />
              <PriceFieldCell
                value={lineItem.unitPrice?.amount ?? 0}
                onChange={(amount) =>
                  onUpdateLineItem(lineItem.id, {
                    unitPrice: {
                      amount,
                      currencyCode:
                        lineItem.unitPrice?.currencyCode ??
                        enumCurrencyCode.USD,
                      __typename: 'Money',
                    },
                  })
                }
                disabled={disabled}
                suffix={pricingUnitSuffix}
              />
              <UnitFieldCell
                value={lineItem.pricingUnit || enumUnit.Pounds}
                onChange={(value) =>
                  onUpdateLineItem(lineItem.id, {
                    pricingUnit: value ?? enumUnit.Pounds,
                  })
                }
                disabled={disabled}
                formatLabel={shortenUnit}
              />

              <WeightFieldCell
                value={toDisplay(grossWeight, displayUnit)}
                suffix={displaySuffix}
                onChange={(value) => handleGrossWeightChange(lineItem, value)}
                disabled={disabled}
              />
              <WeightFieldCell
                value={toDisplay(tareWeight, displayUnit)}
                suffix={displaySuffix}
                onChange={(value) => handleTareWeightChange(lineItem, value)}
                disabled={disabled}
              />
              <WeightFieldCell
                value={toDisplay(netWeight, displayUnit)}
                suffix={displaySuffix}
                disabled
              />

              {!disabled && (
                <DeleteButtonCell
                  onDelete={() => onDeleteLineItem(lineItem.id)}
                  disabled={disabled}
                  className="size-input"
                />
              )}
            </LineItemRow>
          );
        })}
      </div>
    </LineItemTable>
  );
};
